import React, {useState} from 'react';
import Logo from 'assets/images/khula-logo-badge-green.svg';
import TextField from '@material-ui/core/TextField';
import {
  Avatar,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Container,
  CssBaseline,
  FormControlLabel,
  makeStyles,
  Paper,
  Typography,
} from '@material-ui/core';
import {Copyright} from 'screens/Auth/Login/Login';
import {
  apiLOGIN,
  apiResetPassword,
  apiSendResetPasswordEmail,
} from 'constants/Api';
import NavigationService from 'services/NavigationService';

const useStyles = makeStyles((theme) => ({
  paper: {
    marginTop: theme.spacing(8),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    padding: theme.spacing(4),
    paddingTop: theme.spacing(0),
  },
  avatar: {
    width: 128,
    height: 128,
  },
  form: {
    width: '100%', // Fix IE 11 issue.
    marginTop: theme.spacing(3),
  },
  submit: {
    margin: theme.spacing(2, 0, 2),
  },
}));

export default function ForgotPassword() {
  const classes = useStyles();
  const [loading, setLoading] = useState(false);
  const [showResetPasswordForm, setShowResetPasswordForm] = useState(false);
  const [email, setEmail] = useState('');

  const handleSendEmail = (email: string) => {
    setLoading(true);
    new Promise((resolve) => setTimeout(resolve, 1000))
      .then(() => apiSendResetPasswordEmail(email))
      .then((response) => {
        if (!response.success) {
          alert(response.message);
        } else {
          setEmail(email);
          setShowResetPasswordForm(true);
        }
      })
      .catch((sendEmailError) => {
        alert(
          'Unable to send password reset email in at the moment. Please try again later',
        );
        console.log('Failed to send password reset email', sendEmailError);
      })
      .finally(() => setLoading(false));
  };

  const handleResetPassword = (
    resetPasswordToken: string,
    newPassword: string,
  ) => {
    setLoading(true);
    apiResetPassword(email, resetPasswordToken, newPassword)
      .then((response) => {
        if (!response.success) {
          alert(response.message);
        } else {
          return apiLOGIN(email, newPassword).then((response) => {
            if (response.success) {
              NavigationService.navigate('/');
            } else {
              alert(response.message);
            }
          });
        }
      })
      .catch((resetPasswordError) => {
        alert('Unable to reset password at the moment. Please try again later');
        console.log('Failed to reset password', resetPasswordError);
      })
      .finally(() => setLoading(false));
  };

  return (
    <Container className="login-container" component="main" maxWidth="xs">
      <CssBaseline />
      <Paper className={classes.paper} variant="outlined">
        <Avatar className={classes.avatar} src={Logo} variant="square" />
        <Box mb={4} mt={-1}>
          <Typography component="h1" variant="subtitle1">
            Supplier Dashboard
          </Typography>
        </Box>
        <Typography component="h1" variant="h5">
          Reset Password
        </Typography>
        {!showResetPasswordForm && (
          <SendForgotPasswordEmailForm
            onSubmit={handleSendEmail}
            isLoading={loading}
          />
        )}
        {showResetPasswordForm && (
          <>
            <Box mt={3}>
              <Typography component="h1" variant="body2">
                Reset password token sent to your email: {email}
              </Typography>
            </Box>
            <ResetPasswordForm
              onSubmit={handleResetPassword}
              isLoading={loading}
            />
          </>
        )}
      </Paper>
      <Box mt={8}>
        <Copyright />
      </Box>
    </Container>
  );
}

function SendForgotPasswordEmailForm(props: {
  onSubmit: (email: string) => any;
  isLoading: boolean;
}) {
  const classes = useStyles();
  const [email, setEmail] = useState('');

  const handleSubmit = (e: any) => {
    e.preventDefault(); // prevent http post on submit
    props.onSubmit(email);
  };

  return (
    <form className={classes.form} noValidate onSubmit={handleSubmit}>
      <TextField
        variant="outlined"
        margin="normal"
        fullWidth
        id="email"
        label="Email Address"
        name="email"
        autoComplete="email"
        autoFocus
        onChange={(event) => setEmail(event.target?.value)}
      />
      <Button
        fullWidth
        variant="contained"
        color="primary"
        className={classes.submit}
        type="submit"
        disabled={!email}
      >
        {props.isLoading ? (
          <CircularProgress color="inherit" size={24} thickness={6} />
        ) : (
          'Send password reset email'
        )}
      </Button>
    </form>
  );
}

function ResetPasswordForm(props: {
  onSubmit: (resetPasswordToken: string, newPassword: string) => any;
  isLoading: boolean;
}) {
  const classes = useStyles();
  const [resetPasswordToken, setResetPasswordToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = (e: any) => {
    e.preventDefault(); // prevent http post on submit
    props.onSubmit(resetPasswordToken, newPassword);
  };

  return (
    <form className={classes.form} noValidate onSubmit={handleSubmit}>
      <TextField
        variant="outlined"
        margin="normal"
        fullWidth
        id="resetPasswordToken"
        label="Reset Password Token"
        name="Reset Password Token"
        autoFocus
        autoComplete="off"
        onChange={(event) => setResetPasswordToken(event.target?.value)}
      />
      <TextField
        variant="outlined"
        margin="normal"
        fullWidth
        name="password"
        label="New Password"
        type={showPassword ? 'text' : 'password'}
        id="password"
        autoComplete="off"
        onChange={(event) => setNewPassword(event.target?.value)}
      />
      <Box display="flex">
        <FormControlLabel
          control={
            <Checkbox
              color="primary"
              onChange={(event, checked) => setShowPassword(checked)}
            />
          }
          label="Show password"
        />
      </Box>
      <Button
        fullWidth
        variant="contained"
        color="primary"
        className={classes.submit}
        type="submit"
        disabled={!resetPasswordToken || !newPassword}
      >
        {props.isLoading ? (
          <CircularProgress color="inherit" size={24} thickness={6} />
        ) : (
          'Update Password'
        )}
      </Button>
    </form>
  );
}
